"use client";

import Link from "./router-link";
import { ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { services } from "../lib/site-data";

export function ServiceExplorer() {
  const { hash } = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);
  const explorerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeService = services[activeIndex];
  const ActiveIcon = activeService.icon;

  useEffect(() => {
    const index = services.findIndex(
      (service) => service.slug === hash.slice(1),
    );
    if (index < 0) return;

    setActiveIndex(index);
    requestAnimationFrame(() => {
      explorerRef.current?.scrollIntoView({ block: "start" });
    });
  }, [hash]);

  const selectTab = (index: number) => {
    setActiveIndex(index);
    window.history.replaceState(null, "", `#${services[index].slug}`);
  };

  return (
    <div className="service-explorer" ref={explorerRef}>
      <div
        aria-label="核心服务"
        className="service-explorer-nav"
        role="tablist"
      >
        {services.map((service, index) => (
          <button
            aria-controls={`service-panel-${service.slug}`}
            aria-selected={activeIndex === index}
            id={service.slug}
            key={service.title}
            onClick={() => selectTab(index)}
            onFocus={() => setActiveIndex(index)}
            onKeyDown={(event) => {
              if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
              event.preventDefault();
              const nextIndex =
                event.key === "ArrowDown"
                  ? (index + 1) % services.length
                  : (index - 1 + services.length) % services.length;
              setActiveIndex(nextIndex);
              tabRefs.current[nextIndex]?.focus();
            }}
            onMouseEnter={() => setActiveIndex(index)}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            role="tab"
            tabIndex={activeIndex === index ? 0 : -1}
            type="button"
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{service.title}</strong>
            {activeIndex === index && (
              <motion.i layoutId="service-explorer-indicator" />
            )}
          </button>
        ))}
      </div>

      <div className="service-explorer-stage">
        <AnimatePresence initial={false} mode="wait">
          <motion.article
            animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
            aria-labelledby={activeService.slug}
            className="service-explorer-panel"
            exit={{ opacity: 0, y: -16, clipPath: "inset(0 0 14% 0)" }}
            id={`service-panel-${activeService.slug}`}
            initial={{ opacity: 0, y: 22, clipPath: "inset(0 0 18% 0)" }}
            key={activeService.title}
            role="tabpanel"
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="service-explorer-heading">
              <ActiveIcon />
              <span>{String(activeIndex + 1).padStart(2, "0")} / 核心能力</span>
              <h3>{activeService.title}</h3>
            </div>
            <p>{activeService.description}</p>
            <ul>
              {activeService.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Link
              className="text-link"
              href={`/services/${activeService.slug}`}
            >
              查看服务详情 <ArrowUpRight size={16} />
            </Link>
          </motion.article>
        </AnimatePresence>
      </div>
    </div>
  );
}
